import React, {useState} from 'react';
import {
	Button,
	Datagrid,
	EditButton, FileField, FileInput,
	FilterButton,
	FunctionField,
	LoadingPage, required,
	SelectField,
	SelectInput,
	ShowButton,
	TextField,
	TextInput,
	TopToolbar,
	useDataProvider,
	useListContext,
	useNotify,
	usePermissions,
	useRecordContext,
	useRefresh,
	useUpdate,
} from 'react-admin';
import ListMod from '../../Component/Redesign/ListMod';
import {
	USER_STATUS_TYPE,
	UserClaimFilterCollection,
	UserHasFilterCollection,
	UserStatus
} from '../../Utils/constant/Const';
import {Block, Check, Email} from '@mui/icons-material';
import DateFieldMod from '../../Component/Redesign/DateFieldMod';
import {hasAdminRole} from '../../Utils/helpers/permissions';
import DownloadIcon from '@mui/icons-material/GetApp';
import {CircularProgress, Dialog, DialogTitle, IconButton, Menu, MenuItem} from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from '@mui/icons-material/Close';
import {FormProvider, useForm} from "react-hook-form";
import DialogActions from "@mui/material/DialogActions";
import {useNavigate} from "react-router-dom";

const UserStatusActionsButton = () => {
	const record = useRecordContext();
	const refresh = useRefresh();

	const [block] = useUpdate(
		'users/block',
		{id: record?.id || '', data: {}},
		{
			onSuccess: () => {
				refresh()
			},
		}
	);
	const [unblock] = useUpdate(
		'users/unblock',
		{id: record?.id || '', data: {}},
		{
			onSuccess: () => {
				refresh()
			},
		}
	);
	const blockUser = () => {
		block()
	}
	const unblockUser = () => {
		unblock()
	}
	if (!record || !record.id) {
		return null;
	}

	return (record?.status === 1) ?
		<Button children={<Check/>} label="Unblock" onClick={unblockUser}/> :
		<Button children={<Block/>} label="Block" onClick={blockUser}/>

};

const getUserFilters = (permissions) => {
	const commonFilters = [
		<SelectInput label="STATUS" emptyText="ALL" name="status" source="status"
								 choices={UserStatus}
								 alwaysOn/>,
		<SelectInput label="Membership"
								 emptyText="All"
								 name="membership"
								 source="membership"
								 choices={UserHasFilterCollection}
								 alwaysOn/>,
		<SelectInput label="Connected bank Account"
								 emptyText="All"
								 name="bankAccount"
								 source="bankAccount"
								 choices={UserHasFilterCollection}
								 alwaysOn/>,
		<SelectInput label="Social channels"
								 emptyText="All"
								 name="socialChannel"
								 source="socialChannel"
								 choices={UserHasFilterCollection}
								 alwaysOn/>,
		<SelectInput label="Сreated by admin"
								 emptyText="All"
								 name="loginClaimed"
								 source="loginClaimed"
								 sx={{minWidth: 180}}
								 choices={UserClaimFilterCollection}
								 alwaysOn/>
	]
	const adminFilters = [
		<TextInput label="Search" name="search" source="search" alwaysOn/>
	]
	return hasAdminRole(permissions) ? [...commonFilters, ...adminFilters] : [...commonFilters]
}

const SendEmail = () => {
	const record = useRecordContext()
	const [send, {isLoading}] = useUpdate()
	const notify = useNotify()

	const isUnverified = record.status === USER_STATUS_TYPE.unverified
	const isUnclaimed = record.loginClaimed === false

	const sendUnverifiedVerification = () => {
		send(
			'users/resend',
			{id: record.id, meta: {resourceSuffix: ''}},
			{
				onSuccess: () => {
					notify('Email sent')
				},
				onError: (err) => {
					notify(err.message, {type: 'error'})
				}
			}
		)
	}

	const sendClaimVerification = () => {
		send(
			'users/unclaimed',
			{id: record.id, meta: {resourceSuffix: ''}},
			{
				onSuccess: () => {
					notify('Email sent')
				},
				onError: (err) => {
					notify(err.message, {type: 'error'})
				}
			}
		)
	}

	if (!isUnverified && !isUnclaimed) return null

	if (isUnverified) {
		return <Button label="Send Verification" disabled={isLoading} onClick={sendUnverifiedVerification}>
			<Email/>
		</Button>
	}
	return <Button label="Send Invitation" disabled={isLoading} onClick={sendClaimVerification}>
		<Email/>
	</Button>
}

const CreateDropDown = ({ isOpen, onCancel }) => {
	const form = useForm({
		values: {
			file: ''
		},
		mode: 'onChange',
		reValidateMode: 'onChange'
	})
	const notify = useNotify()
	const refresh = useRefresh()
	const [inviteUsers, {isLoading}] = useUpdate();

	const handleCloseModal = () => {
		form.reset()
		onCancel()
	}

	const onSubmit = values => {
		const data = {
			file: values.file,
		}
		inviteUsers(
			'users/list/invite',
			{data, meta: {resourceSuffix: ''}},
			{
				onSuccess: () => {
					notify('Successfully invited')
					refresh()
					handleCloseModal()
				},
				onError: (err) => {
					notify(err.message, {type: 'error'})
					form.setError('file', {type: 'custom', message: err.message})
				}
			})
	};

	const submit = values => {
		onSubmit(values)
	};

	return <Dialog sx={{ width: '50vw', minWidth: '400px', margin: 'auto', '& .MuiPaper-root': { width: '100%' } }} open={isOpen} onClose={handleCloseModal}>
		<DialogTitle sx={{ m: 0, p: 2 }}>Invite Users</DialogTitle>
		<IconButton
			aria-label="close"
			onClick={handleCloseModal}
			sx={theme => ({
				position: 'absolute',
				right: 8,
				top: 8,
				color: theme.palette.grey[500],
			})}
		>
			<CloseIcon />
		</IconButton>
		<DialogContent sx={{ paddingX: 2, paddingTop: 0, paddingBottom: 2 }}>
			<div>
				<h3 style={{ margin: '0 0 12px' }}>Import users with upload file</h3>
				<FormProvider {...form}>
					<form onSubmit={form.handleSubmit(submit)}>
						<FileInput source="file" name="file"
								   helperText={form.getFieldState('file').error?.message}
								   validate={[
									   required(),
									   (value) => {
										   if (!value?.rawFile) return undefined;
										   const file = value.rawFile;
										   const name = file.name.toLowerCase();
										   const type = file.type;

										   const csv = type === 'text/csv' || name.endsWith('.csv');
										   const xlsx = type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || name.endsWith('.xlsx');

										   if (!csv && !xlsx) {
											   return 'Allow only CSV or XLSX files';
										   }

										   return undefined;
									   },
								   ]}
								   accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
						>
							<FileField source="src" title="title"/>
						</FileInput>
						<DialogActions>
							<Button onClick={handleCloseModal}>Cancel</Button>
							<Button variant="contained"
									type="submit"
									color="primary"
									disabled={isLoading || !form.formState.isValid}
									sx={{margin: '0 0 0 8px', '& .MuiButton-icon': { margin: 0 }}}>
								{isLoading && <CircularProgress size={25} thickness={2}/>}
								Save
							</Button>
						</DialogActions>
					</form>
				</FormProvider>
			</div>
		</DialogContent>
	</Dialog>
}

const CustomActions = () => {
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const {permissions} = usePermissions();
	const {sort, filterValues, total} = useListContext();
	const dataProvider = useDataProvider()
	const notify = useNotify()
	const [anchor, setAnchor] = useState(null);
	const navigate = useNavigate();

	const onDownloadExcel = () => {
		dataProvider.uploadFile('users/export/xls', {
			query: {
				filter: filterValues,
				sort: {column: sort.field, direction: sort.order}
			}
		})
			.then(() => {
				notify('Exported successfully', {type: 'info'})
			})
			.catch(() => {
				notify('Export failed', {type: 'error'})
			})
	}

	const toggleCreateModalOpen = () => setIsCreateModalOpen(prev => !prev)

	const handleCreateUserChange = (selected) => {
		if (selected === 'import') {
			toggleCreateModalOpen()

			return
		}

		navigate('/users/create')
	}

	return (
		<TopToolbar>
			<FilterButton/>
			<Button onClick={(e) => setAnchor(e.currentTarget)}>Create</Button>
			<Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
				<MenuItem onClick={() => { handleCreateUserChange('create'); setAnchor(null); }}>Create new user</MenuItem>
				<MenuItem onClick={() => { handleCreateUserChange('import'); setAnchor(null); }}>Import soft profiles</MenuItem>
			</Menu>
			<CreateDropDown isOpen={isCreateModalOpen} onCancel={toggleCreateModalOpen} />
			{
				hasAdminRole(permissions) &&
				<Button
					label={'Export'}
					startIcon={<DownloadIcon/>}
					disabled={total === 0}
					onClick={onDownloadExcel}
				/>
			}
		</TopToolbar>
	);
}

export const UserList = ({...props}) => {

	const {permissions, isLoading} = usePermissions();
	return !isLoading ?
		<ListMod {...props}
						 actions={<CustomActions/>}
						 filters={getUserFilters(permissions)} filterDefaultValues={{status: UserStatus[0].id}}
						 title="User list"
						 hasCreate
						 basePath="/users">
			<Datagrid bulkActionButtons={false}>
				<TextField label={'ID'} source="id" sortBy="id"/>
				{
					hasAdminRole(permissions) && [
						<FunctionField key="avatar" render={record => {
							if (!record.s3Url) return null
							return <img src={record.s3Url} alt={record.first_name}
													style={{width: 30, height: 30, borderRadius: '50%'}}/>
						}}/>,
						<TextField key="first_name" label={'FIRST NAME'} source="first_name"/>,
						<TextField key="last_name" label={'LAST NAME'} source="last_name"/>,
						<TextField key="email" label={'EMAIL'} source="email"/>,
					]
				}

				<SelectField label={'STATUS'} source="status" choices={UserStatus}/>
				<FunctionField label="MEMBERSHIP" render={record => record.activeSubscription ? 'YES' : 'NO'}/>
				<FunctionField label="MARKET VALUE" render={record => record.hasMarketValue ? 'YES' : 'NO'}/>
				<FunctionField label="CREATED BY"
											 render={record => record.loginClaimed ? 'CLAIMED' : record.loginClaimed === false ? 'UNCLAIMED' : '-'}/>
				<DateFieldMod showTime label="CREATED AT" source="created_at"/>
				{
					hasAdminRole(permissions) && <SendEmail/>
				}
				<ShowButton/>
				{
					hasAdminRole(permissions) &&
					<FunctionField label={false}
												 render={record => record.status !== USER_STATUS_TYPE.deleted ? <EditButton/> : null}/>
				}
				{
					hasAdminRole(permissions) && <UserStatusActionsButton/>
				}
			</Datagrid>
		</ListMod> :
		<LoadingPage/>
};
